import { api, ApiError } from './api';
import type { Cart, AddProductToCartRequest, AddTcgToCartRequest, UpdateCartItemRequest } from '@/types';

class CartService {
  async getCart(): Promise<Cart> {
    try {
      return await api.get<Cart>('/cart');
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Authentication required');
        }
        throw error;
      }
      throw new Error('Failed to fetch cart');
    }
  }

  async addProduct(data: AddProductToCartRequest): Promise<void> {
    try {
      await api.post('/cart/product', data);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Authentication required');
        }
        if (error.status === 404) {
          throw new Error('Product not found');
        }
        if (error.status === 400) {
          throw new Error('Invalid request. Please check the quantity and product availability.');
        }
        throw error;
      }
      throw new Error('Failed to add product to cart');
    }
  }

  async addTcg(data: AddTcgToCartRequest): Promise<void> {
    try {
      await api.post('/cart/tcg', data);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Authentication required');
        }
        if (error.status === 404) {
          throw new Error('Listing not found');
        }
        if (error.status === 400) {
          throw new Error('Invalid request. Please check the quantity and listing availability.');
        }
        throw error;
      }
      throw new Error('Failed to add TCG item to cart');
    }
  }

  async updateItem(itemId: string, data: UpdateCartItemRequest): Promise<void> {
    try {
      await api.put(`/cart/items/${itemId}`, data);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Authentication required');
        }
        if (error.status === 400) {
          throw new Error('Invalid quantity or item no longer available');
        }
        throw error;
      }
      throw new Error('Failed to update cart item');
    }
  }

  async removeItem(itemId: string): Promise<void> {
    try {
      await api.delete(`/cart/items/${itemId}`);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Authentication required');
        }
        throw error;
      }
      throw new Error('Failed to remove cart item');
    }
  }

  async clearCart(): Promise<void> {
    try {
      await api.delete('/cart');
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 401) {
          throw new Error('Authentication required');
        }
        throw error;
      }
      throw new Error('Failed to clear cart');
    }
  }
}

export const cartService = new CartService();
